import EyeIcon from "@/components/icons/eye"
import { InputProps } from "@/types/component"
import { maskPhoneNumber } from "@/utils"

import "./index.scss"

import * as React from "react"
import { EyeSecret } from "@/components/icons"

const CopyIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => {
  const { height = 24, width = 24 } = props
  return (
    <svg
      width={width}
      {...props}
      height={height}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.5"
      >
        <path d="M16 3H4v13" />
        <path d="M8 7h12v12a2 2 0 0 1-2 2h-8a2 2 0 0 1-2-2z" />
      </g>
    </svg>
  )
}

const Input: React.FC<InputProps> = ({
  disabled = false,
  error = false,
  errorMessage,
  label,
  required = false,
  onChange,
  placeholder,
  readOnly = false,
  showCopyButton = false,
  startIcon,
  type = "text",
  value: propValue = "",
  variant = "standard",
  ...props
}) => {
  const [focused, setFocused] = React.useState(false)
  const [hovered, setHovered] = React.useState(false)
  const [value, setValue] = React.useState<string>(() =>
    type === "tel" ? maskPhoneNumber(String(propValue)) : String(propValue)
  )
  const [showPassword, setShowPassword] = React.useState(false)

  React.useEffect(() => {
    setValue(type === "tel" ? maskPhoneNumber(String(propValue)) : String(propValue))
  }, [propValue, type])

  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value)
      alert("Copied to clipboard")
    }
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = event.target.value
    const unmaskedValue = currentValue.replace(/\D/g, "")

    if (type === "tel") {
      setValue(maskPhoneNumber(currentValue))
      if (onChange) {
        const syntheticEvent = { ...event }
        syntheticEvent.target.value = unmaskedValue
        onChange(syntheticEvent)
      }
    } else {
      setValue(currentValue)
      if (onChange) {
        onChange(event)
      }
    }
  }

  const renderInput = () => {
    const inputProps = {
      className: `input ${startIcon ? "with-start-icon" : ""} ${showCopyButton || type === "password" ? "no-right-border-radius" : ""}`,
      disabled,
      readOnly,
      onBlur: () => setFocused(false),
      onChange: handleChange,
      onFocus: () => setFocused(true),
      placeholder,
      value,
      ...props,
    }

    return <input {...inputProps} type={type === "password" && showPassword ? "text" : type} />
  }

  return (
    <div
      className={`input-container ${variant} ${focused ? "focused" : ""} ${hovered ? "hovered" : ""} ${error ? "error" : ""} ${disabled ? "disabled" : ""} ${readOnly ? "read-only" : ""} ${startIcon ? "has-start-icon" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {label && (
        <label
          className={`input-label ${focused ? "focused" : ""} ${error ? "error" : ""} ${disabled ? "disabled" : ""} ${readOnly ? "read-only" : ""} ${required ? "required" : ""}`}
        >
          {label}
        </label>
      )}
      {type === "password" ? (
        <div className="input-wrapper">
          {startIcon && <span className="start-icon">{startIcon}</span>}
          {renderInput()}
          <button className="eye-button" onClick={toggleShowPassword} type="button">
            {showPassword ? <EyeIcon height={24} width={24} /> : <EyeSecret height={24} width={24} />}
          </button>
        </div>
      ) : showCopyButton ? (
        <div className="input-wrapper">
          {startIcon && <span className="start-icon">{startIcon}</span>}
          {renderInput()}
          <button className="copy-button" onClick={handleCopy} type="button">
            <CopyIcon height={23} width={23} />
          </button>
        </div>
      ) : startIcon ? (
        <div className="input-wrapper">
          <span className="start-icon">{startIcon}</span>
          {renderInput()}
        </div>
      ) : (
        renderInput()
      )}
      {error && errorMessage && <span className="input-error">{errorMessage}</span>}
    </div>
  )
}

export default Input